import { sanitizeWordHtml } from './wordHtml';

/**
 * Чтение документа Word прямо на телефоне.
 *
 * **Зачем вообще.** До этого `.docx` открывался единственным способом — системным
 * «Поделиться» в Word или Pages: три касания и уход из приложения, тогда как в суде
 * счёт идёт на секунды ([CLIENT.md](docs/CLIENT.md), контекст 2). Решение владельца
 * 10.09.2026: документ обязан открываться одним касанием, не выходя из приложения
 * ([D-60](docs/DECISIONS.md)).
 *
 * **Что показывается.** Текст, заголовки, списки, таблицы, начертания и вложенные
 * картинки — перетекающие по ширине экрана. Это **не** страницы Word: разбиения
 * на листы, колонтитулов и точных отступов здесь нет, и обещать их нечем. Для случая
 * «нужно ровно как в Word» остаётся «Поделиться» — оно никуда не делось.
 *
 * **Только `.docx`.** Старый `.doc` (Word 97–2003) — другой, двоичный формат, и эта
 * библиотека его не читает. Такой файл по-прежнему уходит в другое приложение,
 * и юристу об этом говорится словами, а не пустым экраном.
 *
 * Библиотека грузится **по требованию**, как pdf.js: она весит около 600 КБ, а нужна
 * только тому, кто открыл документ Word. Список дел не платит за неё своим запуском
 * ([NFR.md](docs/NFR.md)).
 */

/** Расширения, которые умеет читать `mammoth`. `.doc` в этот список не входит. */
const READABLE = /\.docx$/i;

const WORD_MIME = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-word.document.macroenabled.12',
]);

/**
 * Тот ли это файл, который мы умеем показать.
 *
 * Спрашиваются оба признака. Тип приходит от системы и на iPhone у файла из «Файлов»
 * бывает пустым — тогда в базу он лёг как `application/octet-stream`, и опознать
 * документ можно только по имени. Имя же в хранилище всегда с расширением: его
 * сохраняет `fileStore.store`.
 */
export const isWordDocument = (mimeType: string, fileName: string): boolean =>
  WORD_MIME.has(mimeType.toLowerCase()) || READABLE.test(fileName);

/**
 * Читает `.docx` и отдаёт разметку, готовую к показу.
 *
 * `null` — «показать не смогли»: файл повреждён, это старый `.doc` или библиотека
 * не поднялась. Экран в этом случае честно предлагает открыть документ в другом
 * приложении, а не показывает пустоту.
 */
export async function readWordDocument(content: Blob): Promise<string | null> {
  try {
    const mammoth = await import('mammoth');
    const { value } = await mammoth.convertToHtml({ arrayBuffer: await content.arrayBuffer() });
    const html = sanitizeWordHtml(value).trim();

    // Пустая разметка — тоже неудача: пустой экран юрист прочтёт как поломку.
    return html.length > 0 ? html : null;
  } catch {
    return null;
  }
}
